import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Sparkles, ChefHat, Scissors, Palette, Eye, Heart, Plane, Leaf, ShoppingBag,
  GraduationCap, PartyPopper, Settings, Save, RefreshCw, Image, FileText,
  User, Utensils, Accessibility, Music, Star, Check, X, Edit2, Plus
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// Available defaults that users can save
const AVAILABLE_DEFAULTS = [
  { id: "face", label: "Face", description: "Skin type, foundation shade, makeup preferences", icon: Sparkles, color: "bg-pink-100 text-pink-600" },
  { id: "hair", label: "Hair", description: "Hair type, texture, chemical history", icon: Scissors, color: "bg-amber-100 text-amber-600" },
  { id: "nails", label: "Nails", description: "Nail shape, length, preferences", icon: Palette, color: "bg-purple-100 text-purple-600" },
  { id: "lash", label: "Lash", description: "Lash type, eye shape, sensitivity", icon: Eye, color: "bg-violet-100 text-violet-600" },
  { id: "dietary", label: "Dietary", description: "Dietary restrictions, allergies, preferences", icon: Utensils, color: "bg-orange-100 text-orange-600" },
  { id: "accessibility", label: "Accessibility", description: "Mobility, hearing, vision needs", icon: Accessibility, color: "bg-blue-100 text-blue-600" },
  { id: "wellness", label: "Wellness", description: "Health goals, medical conditions", icon: Heart, color: "bg-emerald-100 text-emerald-600" },
  { id: "style", label: "Style", description: "Size, brand preferences, budget", icon: ShoppingBag, color: "bg-indigo-100 text-indigo-600" },
];

// Available inspo folders
const AVAILABLE_INSPO_FOLDERS = [
  { id: "face", label: "Face Inspo", description: "Makeup looks, skin goals", icon: Sparkles },
  { id: "hair", label: "Hair Inspo", description: "Hairstyles, colors", icon: Scissors },
  { id: "nails", label: "Nail Inspo", description: "Nail art, designs", icon: Palette },
  { id: "lash", label: "Lash Inspo", description: "Lash styles", icon: Eye },
  { id: "style", label: "Style Inspo", description: "Outfits, fashion", icon: ShoppingBag },
  { id: "culinary", label: "Culinary Inspo", description: "Food, presentations", icon: ChefHat },
  { id: "wellness", label: "Wellness Inspo", description: "Fitness, health goals", icon: Heart },
  { id: "events", label: "Event Inspo", description: "Decor, themes", icon: PartyPopper },
];

// Available intake question categories
const AVAILABLE_INTAKE_QUESTIONS = [
  { id: "allergies", label: "Allergies", description: "Product/food allergies for safety" },
  { id: "appointment_preferences", label: "Appointment Preferences", description: "Time, location preferences" },
  { id: "special_requests", label: "Special Requests", description: "Custom notes for provider" },
  { id: "event_info", label: "Event Info", description: "What's the occasion?" },
  { id: "budget", label: "Budget Range", description: "Price range for services" },
  { id: "referral", label: "Referral Source", description: "How did you hear about us?" },
  { id: "medical_conditions", label: "Medical Conditions", description: "Health considerations" },
  { id: "guest_count", label: "Guest Count", description: "Number of guests/servings" },
  { id: "fitness_level", label: "Fitness Level", description: "Experience and ability" },
  { id: "questions_for_host", label: "Questions for Host", description: "Pre-session questions" },
];

// Default configurations per pilot category
const PILOT_DEFAULTS = {
  makeup: {
    name: "Makeup",
    icon: "Sparkles",
    requested_defaults: ["face", "accessibility"],
    inspo_folders: ["face"],
    intake_questions: ["allergies", "event_info", "special_requests"],
    card_label: "BEAUTY CLIENT CARD",
    dashboard_type: "provider"
  },
  hair: {
    name: "Hair",
    icon: "Scissors",
    requested_defaults: ["hair", "accessibility"],
    inspo_folders: ["hair"],
    intake_questions: ["allergies", "appointment_preferences", "special_requests"],
    card_label: "BEAUTY CLIENT CARD",
    dashboard_type: "provider"
  },
  nails: {
    name: "Nails",
    icon: "Palette",
    requested_defaults: ["nails", "accessibility"],
    inspo_folders: ["nails"],
    intake_questions: ["allergies", "event_info", "special_requests"],
    card_label: "BEAUTY CLIENT CARD",
    dashboard_type: "provider"
  },
  lash: {
    name: "Lashes",
    icon: "Eye",
    requested_defaults: ["lash", "accessibility"],
    inspo_folders: ["lash"],
    intake_questions: ["allergies", "special_requests"],
    card_label: "BEAUTY CLIENT CARD",
    dashboard_type: "provider"
  },
  culinary: {
    name: "Culinary",
    icon: "ChefHat",
    requested_defaults: ["dietary"],
    inspo_folders: ["culinary"],
    intake_questions: ["allergies", "guest_count", "event_info", "special_requests"],
    card_label: "GUEST CARD",
    dashboard_type: "provider"
  },
  wellness: {
    name: "Wellness",
    icon: "Heart",
    requested_defaults: ["wellness", "dietary", "accessibility"],
    inspo_folders: ["wellness"],
    intake_questions: ["medical_conditions", "fitness_level", "special_requests"],
    card_label: "WELLNESS CARD",
    dashboard_type: "provider"
  },
  events: {
    name: "Events",
    icon: "PartyPopper",
    requested_defaults: ["dietary", "accessibility"],
    inspo_folders: ["events"],
    intake_questions: ["special_requests"],
    card_label: "ATTENDEE CARD",
    dashboard_type: "event",
    collect_interests: true,
    collect_vibe: true,
    collect_music: true
  },
  instructor: {
    name: "Instructor",
    icon: "GraduationCap",
    requested_defaults: ["accessibility"],
    inspo_folders: [],
    intake_questions: ["fitness_level", "medical_conditions", "questions_for_host"],
    card_label: "PARTICIPANT CARD",
    dashboard_type: "event",
    collect_interests: true,
    collect_music: false
  },
  style: {
    name: "Style",
    icon: "ShoppingBag",
    requested_defaults: ["style", "accessibility"],
    inspo_folders: ["style"],
    intake_questions: ["budget", "event_info", "special_requests"],
    card_label: "SHOPPER CARD",
    dashboard_type: "provider"
  },
  travel: {
    name: "Travel",
    icon: "Plane",
    requested_defaults: ["dietary", "accessibility"],
    inspo_folders: [],
    intake_questions: ["budget", "special_requests"],
    card_label: "TRAVELER CARD",
    dashboard_type: "provider"
  },
  dispensary: {
    name: "Dispensary",
    icon: "Leaf",
    requested_defaults: [],
    inspo_folders: [],
    intake_questions: ["medical_conditions", "special_requests"],
    card_label: "MEMBER CARD",
    dashboard_type: "provider"
  }
};

export default function PilotConfigEditor({ pilotId, onSave, onClose }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("defaults");

  useEffect(() => {
    fetchPilotConfig();
  }, [pilotId]);

  const fetchPilotConfig = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/api/admin/pilot-config/${pilotId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setConfig(data);
      } else {
        // Use defaults if no config exists
        setConfig(PILOT_DEFAULTS[pilotId] || {
          requested_defaults: [],
          inspo_folders: [],
          intake_questions: [],
          card_label: "CLIENT CARD",
          dashboard_type: "provider"
        });
      }
    } catch (err) {
      console.error("Failed to fetch pilot config:", err);
      setConfig(PILOT_DEFAULTS[pilotId] || {});
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/api/admin/pilot-config/${pilotId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(config)
      });
      
      if (res.ok) {
        toast.success("Pilot configuration saved!");
        onSave?.(config);
      } else {
        toast.error("Failed to save configuration");
      }
    } catch (err) {
      toast.error("Failed to save configuration");
    } finally {
      setSaving(false);
    }
  };

  const toggleDefault = (defaultId) => {
    setConfig(prev => ({
      ...prev,
      requested_defaults: prev.requested_defaults?.includes(defaultId)
        ? prev.requested_defaults.filter(d => d !== defaultId)
        : [...(prev.requested_defaults || []), defaultId]
    }));
  };

  const toggleInspoFolder = (folderId) => {
    setConfig(prev => ({
      ...prev,
      inspo_folders: prev.inspo_folders?.includes(folderId)
        ? prev.inspo_folders.filter(f => f !== folderId)
        : [...(prev.inspo_folders || []), folderId]
    }));
  };

  const toggleIntakeQuestion = (questionId) => {
    setConfig(prev => ({
      ...prev,
      intake_questions: prev.intake_questions?.includes(questionId)
        ? prev.intake_questions.filter(q => q !== questionId)
        : [...(prev.intake_questions || []), questionId]
    }));
  };

  const resetToDefaults = () => {
    if (window.confirm("Reset to default configuration for this pilot?")) {
      setConfig(PILOT_DEFAULTS[pilotId] || {});
      toast.success("Reset to defaults");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="pilot-config-editor">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold capitalize">{pilotId} Pilot Configuration</h3>
          <p className="text-sm text-gray-500">Configure defaults, inspo folders, and intake questions</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={resetToDefaults}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Reset to Defaults
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save Configuration
          </Button>
        </div>
      </div>

      {/* Configuration Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-4 w-full">
          <TabsTrigger value="defaults" className="gap-2">
            <User className="w-4 h-4" />
            Requested Defaults
          </TabsTrigger>
          <TabsTrigger value="inspo" className="gap-2">
            <Image className="w-4 h-4" />
            Inspo Folders
          </TabsTrigger>
          <TabsTrigger value="intake" className="gap-2">
            <FileText className="w-4 h-4" />
            Intake Questions
          </TabsTrigger>
          <TabsTrigger value="display" className="gap-2">
            <Settings className="w-4 h-4" />
            Display Settings
          </TabsTrigger>
        </TabsList>

        {/* Requested Defaults Tab */}
        <TabsContent value="defaults" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Requested Defaults</CardTitle>
              <CardDescription>
                Select which saved defaults to request from clients. These are pre-saved preferences that carry over between bookings.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {AVAILABLE_DEFAULTS.map(defaultOption => {
                const isSelected = config?.requested_defaults?.includes(defaultOption.id);
                const Icon = defaultOption.icon;
                
                return (
                  <div
                    key={defaultOption.id}
                    onClick={() => toggleDefault(defaultOption.id)}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      isSelected ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-lg ${defaultOption.color} flex items-center justify-center`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium">{defaultOption.label}</p>
                      <p className="text-sm text-gray-500">{defaultOption.description}</p>
                    </div>
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                      isSelected ? 'bg-purple-500 text-white' : 'bg-gray-200'
                    }`}>
                      {isSelected && <Check className="w-4 h-4" />}
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Inspo Folders Tab */}
        <TabsContent value="inspo" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Inspo Folders</CardTitle>
              <CardDescription>
                Select which inspiration folders clients can add images to for this pilot.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-3">
              {AVAILABLE_INSPO_FOLDERS.map(folder => {
                const isSelected = config?.inspo_folders?.includes(folder.id);
                const Icon = folder.icon;
                
                return (
                  <div
                    key={folder.id}
                    onClick={() => toggleInspoFolder(folder.id)}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <Icon className={`w-5 h-5 ${isSelected ? 'text-blue-600' : 'text-gray-400'}`} />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm">{folder.label}</p>
                      <p className="text-xs text-gray-500 truncate">{folder.description}</p>
                    </div>
                    {isSelected && <Check className="w-4 h-4 text-blue-600" />}
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Intake Questions Tab */}
        <TabsContent value="intake" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Intake Questions</CardTitle>
              <CardDescription>
                Select which questions to ask during booking. These are NOT from saved defaults - they're specific to this booking.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {AVAILABLE_INTAKE_QUESTIONS.map(question => {
                const isSelected = config?.intake_questions?.includes(question.id);
                
                return (
                  <div
                    key={question.id}
                    onClick={() => toggleIntakeQuestion(question.id)}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      isSelected ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <div className={`w-6 h-6 rounded flex items-center justify-center ${
                      isSelected ? 'bg-green-500 text-white' : 'bg-gray-200'
                    }`}>
                      {isSelected && <Check className="w-4 h-4" />}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium">{question.label}</p>
                      <p className="text-sm text-gray-500">{question.description}</p>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Display Settings Tab */}
        <TabsContent value="display" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Display Settings</CardTitle>
              <CardDescription>
                Configure how this pilot appears to clients
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Card Label</Label>
                <Input
                  value={config?.card_label || ""}
                  onChange={(e) => setConfig(prev => ({ ...prev, card_label: e.target.value }))}
                  placeholder="e.g., BEAUTY CLIENT CARD"
                />
                <p className="text-xs text-gray-500 mt-1">Label shown on client preference cards</p>
              </div>
              
              <div>
                <Label>Dashboard Type</Label>
                <Select 
                  value={config?.dashboard_type || "provider"}
                  onValueChange={(v) => setConfig(prev => ({ ...prev, dashboard_type: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="provider">Provider (1-on-1 bookings)</SelectItem>
                    <SelectItem value="event">Event (group RSVPs)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {config?.dashboard_type === "event" && (
                <>
                  <div className="flex items-center justify-between p-3 rounded-lg border">
                    <div>
                      <p className="font-medium">Collect Interests</p>
                      <p className="text-sm text-gray-500">Ask about attendee interests</p>
                    </div>
                    <Switch
                      checked={config?.collect_interests || false}
                      onCheckedChange={(v) => setConfig(prev => ({ ...prev, collect_interests: v }))}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between p-3 rounded-lg border">
                    <div>
                      <p className="font-medium">Collect Vibe</p>
                      <p className="text-sm text-gray-500">Ask about social preferences</p>
                    </div>
                    <Switch
                      checked={config?.collect_vibe || false}
                      onCheckedChange={(v) => setConfig(prev => ({ ...prev, collect_vibe: v }))}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between p-3 rounded-lg border">
                    <div>
                      <p className="font-medium">Collect Music (Tap In)</p>
                      <p className="text-sm text-gray-500">Enable music preference collection</p>
                    </div>
                    <Switch
                      checked={config?.collect_music || false}
                      onCheckedChange={(v) => setConfig(prev => ({ ...prev, collect_music: v }))}
                    />
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Summary */}
      <Card className="bg-gray-50">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Configuration Summary</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <div className="flex items-center gap-2">
            <User className="w-4 h-4 text-purple-500" />
            <span className="text-gray-600">Defaults:</span>
            {config?.requested_defaults?.length > 0 ? (
              <div className="flex gap-1 flex-wrap">
                {config.requested_defaults.map(d => (
                  <Badge key={d} variant="secondary" className="capitalize">{d}</Badge>
                ))}
              </div>
            ) : (
              <span className="text-gray-400">None</span>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            <Image className="w-4 h-4 text-blue-500" />
            <span className="text-gray-600">Inspo Folders:</span>
            {config?.inspo_folders?.length > 0 ? (
              <div className="flex gap-1 flex-wrap">
                {config.inspo_folders.map(f => (
                  <Badge key={f} variant="secondary" className="capitalize">{f}</Badge>
                ))}
              </div>
            ) : (
              <span className="text-gray-400">None</span>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-green-500" />
            <span className="text-gray-600">Intake:</span>
            {config?.intake_questions?.length > 0 ? (
              <div className="flex gap-1 flex-wrap">
                {config.intake_questions.map(q => (
                  <Badge key={q} variant="secondary" className="capitalize">{q.replace('_', ' ')}</Badge>
                ))}
              </div>
            ) : (
              <span className="text-gray-400">None</span>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Export the default configurations for use elsewhere
export { PILOT_DEFAULTS, AVAILABLE_DEFAULTS, AVAILABLE_INSPO_FOLDERS, AVAILABLE_INTAKE_QUESTIONS };
